<template>
  <button :type="type" :class="buttonClass" :disabled="disabled" @click="emit('click', $event)">
    <i v-if="icon" :class="iconClass" class="me-2"></i>
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ButtonProps } from './types';

const props = withDefaults(defineProps<ButtonProps>(), {
  type: 'button',
  variant: 'primary',
  buttonStyle: 'filled',
  icon: undefined,
  disabled: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClass = computed(() => {
  const variant = props.variant;
  const style = props.buttonStyle;

  const variantClass = `custom-btn--${variant}`;
  const styleClass = `custom-btn--${style}`;

  const classes = [
    'btn',
    'custom-btn',
    'd-flex',
    'align-items-center',
    'justify-content-center',
    'py-3',
    'px-4',
    'size-12',
    'text-decoration-none',
    'transition-all',
    'duration-200',
    'w-285x',
    'h-48x',
    'rounded-5',
    'fw-normal',
    variantClass,
    styleClass,
  ];

  return classes;
});

const iconClass = computed(() => {
  if (!props.icon) {
    return '';
  }

  const iconMap: Record<string, string> = {
    envelope: 'fa-solid fa-envelope',
    'times-circle': 'fa-regular fa-circle-xmark',
    clear: 'fa-regular fa-circle-xmark',
    plus: 'fa-solid fa-plus',
    star: 'fa-regular fa-star',
    check: 'fa-solid fa-check',
  };

  const iconClass = iconMap[props.icon] || `fa-solid fa-${props.icon}`;

  return iconClass;
});
</script>

<style scoped lang="scss">
.custom-btn {
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.custom-btn:focus:not(:focus-visible) {
  outline: none;
  box-shadow: none;
}

.custom-btn:focus-visible {
  outline: 2px solid $primary-blue;
  outline-offset: 2px;
}

.custom-btn--primary {
  &.custom-btn--filled {
    background-color: $primary-blue;
    border-color: $primary-blue;
    color: $white-01;

    &:hover {
      background-color: $blue-12;
      border-color: $blue-12;
      color: $white-01;
    }

    &:active {
      background-color: $blue-12;
      border-color: $blue-12;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $blue-12;
      border-color: $blue-12;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $primary-blue;
      border-color: $primary-blue;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    color: $primary-blue;
    border-color: $primary-blue;
    background-color: transparent;

    &:hover {
      background-color: $primary-blue;
      color: $white-01;
      border-color: $primary-blue;
    }

    &:active {
      background-color: $blue-12;
      border-color: $blue-12;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $primary-blue;
      border-color: $primary-blue;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $primary-blue;
      background-color: transparent;
      border-color: $primary-blue;
    }
  }

  &.custom-btn--no-border {
    color: $primary-blue;
    background-color: transparent;
    border: none;

    &:hover {
      opacity: 0.8;
      color: $primary-blue;
    }

    &:active {
      opacity: 0.6;
      color: $primary-blue;
    }

    &:focus-visible {
      outline: 2px solid $primary-blue;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $primary-blue;
      background-color: transparent;
    }
  }
}

.custom-btn--secondary {
  &.custom-btn--filled {
    background-color: $pink-01;
    border-color: $pink-01;
    color: $white-01;

    &:hover {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:active {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $pink-01;
      border-color: $pink-01;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    color: $pink-01;
    border-color: $pink-01;
    background-color: transparent;

    &:hover {
      background-color: $pink-01;
      color: $white-01;
      border-color: $pink-01;
    }

    &:active {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $pink-01;
      border-color: $pink-01;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $pink-01;
      background-color: transparent;
      border-color: $pink-01;
    }
  }

  &.custom-btn--no-border {
    color: $pink-01;
    background-color: transparent;
    border: none;

    &:hover {
      opacity: 0.8;
      color: $pink-01;
    }

    &:active {
      opacity: 0.6;
      color: $pink-01;
    }

    &:focus-visible {
      outline: 2px solid $pink-01;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $pink-01;
      background-color: transparent;
    }
  }
}

.custom-btn--gray {
  &.custom-btn--filled {
    background-color: $grey-60;
    border-color: $grey-60;
    color: $white-01;

    &:hover {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:active {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $grey-60;
      border-color: $grey-60;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    background-color: transparent;
    border-color: $grey-60;
    color: $grey-60;

    &:hover {
      background-color: $grey-60;
      border-color: $grey-60;
      color: $white-01;
    }

    &:active {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:focus-visible {
      background-color: $grey-60;
      border-color: $grey-60;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $grey-60;
      background-color: transparent;
      border-color: $grey-60;
    }
  }

  &.custom-btn--no-border {
    background-color: transparent;
    border-color: transparent;
    color: $grey-60;

    &:hover {
      opacity: 0.8;
      color: $grey-60;
    }

    &:active {
      opacity: 0.6;
      color: $grey-60;
    }

    &:focus-visible {
      outline: 2px solid $grey-60;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $grey-60;
      background-color: transparent;
    }
  }
}
</style>
